import React, { useState, useEffect } from 'react';
import { Button, Row, Col, Form } from 'react-bootstrap';
import { columnManager } from '../Common/ColumnManager';
import StatFilter, { convertFilterToNote, createFilter } from '../Common/StatFilter';
import { GetPlayers, GetSeasonDates, GetXXBReport } from '../../Services/ApiSvc';
import TabulatorTable from '../Common/TabulatorTable';
import ExcelExporter from '../Common/ExcelExporter';
import PDFExporter from '../Common/PDFExporter';
import { serviceTimeAddition } from '../Common/CommonFunctions';

function FreeAgencyReport() {
    const [allPlayers, setAllPlayers] = useState([]);
    const [columnGroups, setColumnGroups] = useState(null);
    const [data, setData] = useState({
        columns: null,
        exceptionMsg: null,
        loading: false,
        rows: null,
    });
    const [excelNotes, setExcelNotes] = useState([]);
    const [seasonDates, setSeasonDates] = useState([]);

    useEffect(() => {
        (async function Initialize() {
            let apiSeasonDates = (await GetSeasonDates()).reduce((acc, cur) => {
                acc[cur.season] = cur;
                return acc;
            }, {});
            let allPlayersFromAPI = await GetPlayers();
            let groupObj = {
                displayStats: [],
                filters: [],
            };

            let colGroups = await columnManager.getAllGroupsBySection('XXB');

            groupObj.displayStats.contract = colGroups.find(group => group.label === 'XXBReportContract').columns;
            groupObj.displayStats.player = colGroups.find(group => group.label === 'XXBReportPlayer').columns;
            groupObj.filters = colGroups.find(group => group.label === 'XXBReportFilters').columns;
            groupObj.xxbStat = await columnManager.getSingleColumnById(829);

            setSeasonDates(apiSeasonDates);
            setAllPlayers(allPlayersFromAPI);
            setColumnGroups(groupObj);
        })()
    }, []);

    async function getData(formOptions) {
        let reqParams = {
            displayStats: {
                contract: columnGroups.displayStats.contract,
                player: columnGroups.displayStats.player,
            },
            filters: formOptions.filters.slice(),
        }
        reqParams.filters.find(f => f.statId === 903).force = true; // Force filter to be used even if all options are selected.
        setData({
            columns: null,
            exceptionMsg: null,
            loading: true,
            rows: null,
        });
        updateNotes(formOptions);
        let response = await GetXXBReport(reqParams);
        if (((response.rows == null || response.rows.length < 1)
            && (response.exceptionMsgs.contract || response.exceptionMsgs.player.findIndex(msg => msg != null) > -1)
        )) {
            let exResponse = {};
            exResponse.exceptionMsg =
                (response.exceptionMsgs.contract + '.\r\n' ?? '')
                + response.exceptionMsgs.player.filter(msg => msg != null).join('.\r\n');
            setData(exResponse);
            return;
        }
        groupData(response);
    }

    function groupData(resData) {
        let columns = [
            { title: 'Category', field: 'Category', width: 150, hozAlign: 'left', },
            { title: '# Players', field: 'Players', width: 70, },
            Object.assign({},
                columnGroups.displayStats.player.find(column => column.id === 780),
                { title: 'Avg Age (PY+1)', field: 'AvgAge', width: 70, },
            ),
            Object.assign({},
                columnGroups.displayStats.player.find(column => column.id === 780),
                { title: '# MLB Contracts', field: 'MLBContracts', width: 70, formatter: null, },
            ),
            Object.assign({},
                columnGroups.displayStats.player.find(column => column.id === 780),
                { title: '# MiLB Contracts', field: 'MiLBContracts', width: 70, formatter: null, },
            ),
            Object.assign({},
                columnGroups.displayStats.player.find(column => column.id === 903),
            ),
            Object.assign({},
                columnGroups.displayStats.contract.find(column => column.id === 385),
            ),
            Object.assign({},
                columnGroups.displayStats.player.find(column => column.id === 3),
            ),
            Object.assign({},
                columnGroups.displayStats.player.find(column => column.id === 612),
            ),
            //Object.assign({},
            //    columnGroups.displayStats.player.find(column => column.id === 780),
            //    { title: '# Unsigned', field: 'Unsigned', width: 70, formatter: null, },
            //),
            Object.assign({},
                columnGroups.displayStats.contract.find(column => column.id === 519),
                { title: 'Total Term (Exp)', field: 'TermExp', }
            ),
            Object.assign({},
                columnGroups.displayStats.contract.find(column => column.id === 382),
                { title: 'Total YS (Exp)', field: 'YSExp', },
            ),
            Object.assign({},
                columnGroups.displayStats.contract.find(column => column.id === 383),
                { title: 'Total AAV (Exp)', field: 'AAVExp', },
            ),
            Object.assign({},
                columnGroups.displayStats.contract.find(column => column.id === 516),
            ),
            Object.assign({},
                columnGroups.displayStats.contract.find(column => column.id === 519),
                { title: 'Total Term (New)', field: 'TermNew', },
            ),
            Object.assign({},
                columnGroups.displayStats.contract.find(column => column.id === 382),
                { title: 'Total YS (New)', field: 'YSNew', },
            ),
            Object.assign({},
                columnGroups.displayStats.contract.find(column => column.id === 383),
                { title: 'Total AAV (New)', field: 'AAVNew', },
            ),
            Object.assign({},
                columnGroups.displayStats.contract.find(column => column.id === 542),
                { title: 'Total Guaranteed (New)', field: 'GuaranteedNew', },
            ),
            Object.assign({},
                columnGroups.displayStats.player.find(column => column.id === 780),
                { title: 'Total aWAR', field: 'aWAR', width: 70, },
            ),
            Object.assign({},
                columnGroups.displayStats.player.find(column => column.id === 780),
                { title: 'CTD aWAR', field: 'CTDaWAR', width: 70, },
            ),
            Object.assign({},
                columnGroups.displayStats.player.find(column => column.id === 5),
                { title: 'CTD MLS', field: 'CTDMLS', width: 70, },
            ),
            Object.assign({},
                columnGroups.displayStats.player.find(column => column.id === 780),
                { title: 'ZiPS (PY+1)', field: 'zipsWAR', width: 70, },
            ),
            Object.assign({},
                columnGroups.displayStats.contract.find(column => column.id === 382),
                { title: 'YS/ZiPS', field: 'YSZiPS', },
            ),
            Object.assign({},
                columnGroups.displayStats.contract.find(column => column.id === 383),
                { title: 'AAV/ZiPS', field: 'AAVZiPS', },
            ),
            Object.assign({},
                columnGroups.displayStats.contract.find(column => column.id === 519),
                { title: 'AVG Term (Exp)', field: 'AvgTermExp', }
            ),
            Object.assign({},
                columnGroups.displayStats.contract.find(column => column.id === 382),
                { title: 'AVG YS (Exp)', field: 'AvgYSExp', },
            ),
            Object.assign({},
                columnGroups.displayStats.contract.find(column => column.id === 383),
                { title: 'AVG AAV (Exp)', field: 'AvgAAVExp', },
            ),
            Object.assign({},
                columnGroups.displayStats.contract.find(column => column.id === 519),
                { title: 'AVG Term (New)', field: 'AvgTermNew', },
            ),
            Object.assign({},
                columnGroups.displayStats.contract.find(column => column.id === 382),
                { title: 'AVG YS (New)', field: 'AvgYSNew', },
            ),
            Object.assign({},
                columnGroups.displayStats.contract.find(column => column.id === 383),
                { title: 'AVG AAV (New)', field: 'AvgAAVNew', },
            ),
            Object.assign({},
                columnGroups.displayStats.contract.find(column => column.id === 542),
                { title: 'AVG Guaranteed (New)', field: 'AvgGuarNew', },
            ),
        ];
        let posData = {
            H: { Category: 'H', _children: [] },
            RP: { Category: 'RP', _children: [] },
            SP: { Category: 'SP', _children: [] },
            '': { Category: '', _children: [] },
        };
        let signDateData = {
            OctNov: { Category: 'Oct/Nov', _children: [] },
            Dec: { Category: 'Dec', _children: [] },
            Jan: { Category: 'Jan', _children: [] },
            Feb: { Category: 'Feb', _children: [] },
            March: { Category: 'March-OD', _children: [] },
            InSeason: { Category: 'In-Season', _children: [] },
            Unsigned: { Category: 'Unsigned', _children: [] },
        };
        let freeAgentTypeData = {
            XXB: { Category: 'XXB', _children: [] },
            XXD: { Category: 'XXD', _children: [] },
            ForeignPlayer: { Category: 'Foreign Player', _children: [] },
            Extended: { Category: 'Extended', _children: [] },
            NonTendered: { Category: 'Non-Tendered', _children: [] },
        }
        let totalData = {
            Total: { Category: 'Total', _children: [] }
        }

        resData.rows.forEach(row => {
            const adjustedRow = adjustProperties(row);
            totalData.Total = dataAccumulator(totalData.Total, adjustedRow);
            totalData.Total._children.push(adjustedRow);
            switch (row[resData.columnMap.player.PrimaryPosition]) {
                case 'RP':
                    posData.RP = dataAccumulator(posData.RP, adjustedRow);
                    posData.RP._children.push(adjustedRow);
                    break;
                case 'SP':
                    posData.SP = dataAccumulator(posData.SP, adjustedRow);
                    posData.SP._children.push(adjustedRow);
                    break;
                case null:
                    posData[''] = dataAccumulator(posData[''], adjustedRow);
                    posData['']._children.push(adjustedRow);
                    break;
                default:
                    posData.H = dataAccumulator(posData.H, adjustedRow);
                    posData.H._children.push(adjustedRow);
                    break;
            }

            let signDate = new Date(row[resData.columnMap.contract.SigningDate]);
            let signMonth = null;
            if (signDate.getMonth() >= 9 && signDate.getFullYear() === row.PlatformSeason
                || signDate.getMonth() < 9 && signDate.getFullYear() === row.PlatformSeason + 1)
                signMonth = signDate.getMonth();

            switch (signMonth) {
                case 9:
                case 10:
                    signDateData.OctNov = dataAccumulator(signDateData.OctNov, adjustedRow);
                    signDateData.OctNov._children.push(adjustedRow);
                    break;
                case 11:
                    signDateData.Dec = dataAccumulator(signDateData.Dec, adjustedRow);
                    signDateData.Dec._children.push(adjustedRow);
                    break;
                case 0:
                    signDateData.Jan = dataAccumulator(signDateData.Jan, adjustedRow);
                    signDateData.Jan._children.push(adjustedRow);
                    break;
                case 1:
                    signDateData.Feb = dataAccumulator(signDateData.Feb, adjustedRow);
                    signDateData.Feb._children.push(adjustedRow);
                    break;
                case 2:
                case 3:
                    if (signDate < new Date(seasonDates[signDate.getFullYear()].openingDay)) {
                        signDateData.March = dataAccumulator(signDateData.March, adjustedRow);
                        signDateData.March._children.push(adjustedRow);
                    } else {
                        signDateData.InSeason = dataAccumulator(signDateData.InSeason, adjustedRow);
                        signDateData.InSeason._children.push(adjustedRow);
                    }
                    break;
                case 4:
                case 5:
                case 6:
                case 7:
                case 8:
                    signDateData.InSeason = dataAccumulator(signDateData.InSeason, adjustedRow);
                    signDateData.InSeason._children.push(adjustedRow);
                    break;
                default:
                    signDateData.Unsigned = dataAccumulator(signDateData.Unsigned, adjustedRow);
                    signDateData.Unsigned._children.push(adjustedRow);
                    break;
            }

            switch (row[resData.columnMap.player.FreeAgentType]) {
                case 'XXB':
                    freeAgentTypeData.XXB = dataAccumulator(freeAgentTypeData.XXB, adjustedRow);
                    freeAgentTypeData.XXB._children.push(adjustedRow);
                    break;
                case 'XXD':
                    freeAgentTypeData.XXD = dataAccumulator(freeAgentTypeData.XXD, adjustedRow);
                    freeAgentTypeData.XXD._children.push(adjustedRow);
                    break;
                case 'Foreign Player':
                    freeAgentTypeData.ForeignPlayer = dataAccumulator(freeAgentTypeData.ForeignPlayer, adjustedRow);
                    freeAgentTypeData.ForeignPlayer._children.push(adjustedRow);
                    break;
                case 'Extended':
                    freeAgentTypeData.Extended = dataAccumulator(freeAgentTypeData.Extended, adjustedRow);
                    freeAgentTypeData.Extended._children.push(adjustedRow);
                    break;
                case 'Non-Tendered':
                    freeAgentTypeData.NonTendered = dataAccumulator(freeAgentTypeData.NonTendered, adjustedRow);
                    freeAgentTypeData.NonTendered._children.push(adjustedRow);
                    break;
            }
        })

        setData({
            columns: columns,
            exceptionMsg: null,
            loading: false,
            rows: {
                Pos: Object.values(posData).map(row => {
                    row.AvgAge = row.Age / row.Players;
                    row.YSZiPS = row.YSNew / row.zipsWAR;
                    row.AAVZiPS = row.AAVNew / row.zipsWAR;
                    return row;
                }).filter(row => row.Age != null),
                SignDate: Object.values(signDateData).map(row => {
                    row.AvgAge = row.Age / row.Players;
                    row.YSZiPS = row.YSNew / row.zipsWAR;
                    row.AAVZiPS = row.AAVNew / row.zipsWAR;
                    return row;
                }).filter(row => row.Age != null),
                FreeAgentType: Object.values(freeAgentTypeData).map(row => {
                    row.AvgAge = row.Age / row.Players;
                    row.YSZiPS = row.YSNew / row.zipsWAR;
                    row.AAVZiPS = row.AAVNew / row.zipsWAR;
                    return row;
                }).filter(row => row.Age != null),
                Total: Object.values(totalData).map(row => {
                    row.AvgAge = row.Age / row.Players;
                    row.YSZiPS = row.YSNew / row.zipsWAR;
                    row.AAVZiPS = row.AAVNew / row.zipsWAR;
                    return row;
                }).filter(row => row.Age != null),
            }
        });

        function adjustProperties(row) {
            let retRow = Object.assign({}, row);

            retRow.Category = row[resData.columnMap.player.Player];
            retRow.Players = 1;
            retRow.SigningDate = row[resData.columnMap.contract.ReportDate] || row[resData.columnMap.contract.SigningDate]
            retRow.Team = row[resData.columnMap.player.Team]
            retRow.SignTeam = row[resData.columnMap.contract.SignTeam]
            retRow.PrimaryPosition = row[resData.columnMap.player.PrimaryPosition]
            retRow.AvgAge = nullToZero(row[resData.columnMap.player.Age]) + 1;
            if (row[resData.columnMap.contract.ContractLeague] === 'Minor' || row[resData.columnMap.contract.ReportDate]){
                retRow.MiLBContracts = 1;
                retRow.MLBContracts = 0;
            } else if (row[resData.columnMap.contract.SigningDate] != null) {
                retRow.MLBContracts = 1;
                retRow.MiLBContracts = 0;
            } else {
                retRow.MLBContracts = 0;
                retRow.MiLBContracts = 0;
            }
            retRow.FreeAgentType = row[resData.columnMap.player.FreeAgentType];
            retRow.TermExp = nullToZero(row[resData.columnMap.player.Term]);
            retRow.YSExp = nullToZero(row[resData.columnMap.player.YS]);
            retRow.AAVExp = nullToZero(row[resData.columnMap.player.AAV]);
            retRow.TermNew = nullToZero(row[resData.columnMap.contract.Term]);
            retRow.YSNew = nullToZero(row[resData.columnMap.contract.YS]);
            retRow.AAVNew = nullToZero(row[resData.columnMap.contract.AAV]);
            retRow.GuaranteedNew = nullToZero(row[resData.columnMap.contract.TotalGuaranteed]);
            retRow.aWAR = nullToZero(row[resData.columnMap.player.aWAR]);
            retRow.CTDaWAR = nullToZero(row[resData.columnMap.player['Career To Date aWAR']]);
            retRow.CTDMLS = nullToZero(row[resData.columnMap.player['Career To Date MLS']]);
            retRow.zipsWAR = nullToZero(row[resData.columnMap.player['zipsWAR as Batter']])
                + nullToZero(row[resData.columnMap.player['zipsWAR as Pitcher']]);
            retRow.YSZiPS = retRow.YSNew / retRow.zipsWAR;
            retRow.AAVZiPS = retRow.AAVNew / retRow.zipsWAR;

            return retRow;
        }

        function dataAccumulator(acc, cur) {
            let retData = {};

            retData.Category = acc.Category;
            retData._children = acc._children;
            retData.Players = nullToZero(acc.Players) + 1;
            retData.Age = nullToZero(acc.Age) + nullToZero(cur[resData.columnMap.player.Age]) + 1;
            retData.MLBContracts = nullToZero(acc.MLBContracts) + nullToZero(cur.MLBContracts);
            retData.MiLBContracts = nullToZero(acc.MiLBContracts) + nullToZero(cur.MiLBContracts);
            retData.Unsigned = nullToZero(acc.Unsigned) +
                (cur[resData.columnMap.player.Age] == null ? 1 : 0);
            retData.TermExp = nullToZero(acc.TermExp) + nullToZero(cur[resData.columnMap.player.Term]);
            retData.YSExp = nullToZero(acc.YSExp) + nullToZero(cur[resData.columnMap.player.YS]);
            retData.AAVExp = nullToZero(acc.AAVExp) + nullToZero(cur[resData.columnMap.player.AAV]);
            retData.TermNew = nullToZero(acc.TermNew) + nullToZero(cur[resData.columnMap.contract.Term]);
            retData.YSNew = nullToZero(acc.YSNew) + nullToZero(cur[resData.columnMap.contract.YS]);
            retData.AAVNew = nullToZero(acc.AAVNew) + nullToZero(cur[resData.columnMap.contract.AAV]);
            retData.GuaranteedNew = nullToZero(acc.GuaranteedNew) + nullToZero(cur[resData.columnMap.contract.TotalGuaranteed]);
            retData.aWAR = nullToZero(acc.aWAR) + nullToZero(cur[resData.columnMap.player.aWAR]);
            retData.CTDaWAR = nullToZero(acc.CTDaWAR) + nullToZero(cur[resData.columnMap.player['Career To Date aWAR']]);
            retData.CTDMLS = serviceTimeAddition(nullToZero(acc.CTDMLS), nullToZero(cur[resData.columnMap.player['Career To Date MLS']]));
            retData.zipsWAR = nullToZero(acc.zipsWAR) + nullToZero(cur[resData.columnMap.player['zipsWAR as Batter']])
                + nullToZero(cur[resData.columnMap.player['zipsWAR as Pitcher']]);

            retData.AvgTermExp = (nullToZero(retData.TermExp) / nullToZero(retData.Players)).toFixed(2);
            retData.AvgYSExp = (nullToZero(retData.YSExp) / nullToZero(retData.Players)).toFixed(2);
            retData.AvgAAVExp = (nullToZero(retData.AAVExp) / nullToZero(retData.Players)).toFixed(2);
            retData.AvgTermNew = (nullToZero(retData.TermNew) / nullToZero(retData.Players)).toFixed(2);
            retData.AvgYSNew = (nullToZero(retData.YSNew) / nullToZero(retData.Players)).toFixed(2);
            retData.AvgAAVNew = (nullToZero(retData.AAVNew) / nullToZero(retData.Players)).toFixed(2);
            retData.AvgGuarNew = (nullToZero(retData.GuaranteedNew) / nullToZero(retData.Players)).toFixed(2);

            return retData;
        }

        function nullToZero(data) {
            if (data == null || isNaN(data))
                return 0;
            return Number(data);
        }
    }

    function updateNotes(formOptions) {
        let notes = {
            formNotes: [],
        };

        if (formOptions) {
            formOptions.filters.forEach(filter => {
                if (filter.value) {
                    let filterNote = convertFilterToNote(filter);
                    if (filterNote)
                        notes.formNotes.push(filterNote);
                }
            });
        }

        setExcelNotes(notes);
    }

    return (
        <div className='body xxb-report pdfSection'>
            <div className='pdfChunk'>
                <h2>Free Agency Report</h2>
                <RecentSigningsForm
                    allPlayers={allPlayers}
                    filters={columnGroups?.filters}
                    submit={getData}
                    exporters={
                        data.rows &&
                        <React.Fragment>
                            <ExcelExporter
                                    columns={{
                                        Pos: data.columns,
                                        SignDate: data.columns,
                                        Total: data.columns,
                                    }}
                                    data={data.rows}
                                    notes={{
                                        Pos: excelNotes,
                                        SignDate: excelNotes,
                                        Total: excelNotes,
                                    }}
                                    reportName={'XXBReport'}
                                    sheetNames={['Pos', 'SignDate', 'Total' ]}
                            />
                            <PDFExporter fileName='XXBReport' />
                        </React.Fragment>
                    }
                />
            </div>
            {
                data?.loading &&
                <div>
                    <br />
                    <br />
                    <i className='fa fa-spinner fa-spin loading-icon'></i>
                    <br />
                </div>
            }
            {
                data?.rows &&
                <>
                    <div
                        className='pdfChunk tableChunk'
                        style={{
                            width: (data.columns.reduce((acc, cur) => acc + ((cur.width) > 20 ? (cur.width) : 20), 0)) + 4
                        }}
                    >
                        <TabulatorTable
                            cols={data.columns}
                            data={data.rows.Pos}
                            dataTreeElementColumn={'Category'}
                            initSort={[{ column: 'SigningDate', dir: 'desc' }]}
                        />
                        <br />
                    </div>
                    <div
                        className='pdfChunk tableChunk'
                        style={{
                            width: (data.columns.reduce((acc, cur) => acc + ((cur.width) > 20 ? (cur.width) : 20), 0)) + 4
                        }}
                    >
                        <TabulatorTable
                            cols={data.columns}
                            data={data.rows.SignDate}
                            dataTreeElementColumn={'Category'}
                            initSort={[{ column: 'SigningDate', dir: 'desc' }]}
                        />
                        <br />
                    </div>
                    <div
                        className='pdfChunk tableChunk'
                        style={{
                            width: (data.columns.reduce((acc, cur) => acc + ((cur.width) > 20 ? (cur.width) : 20), 0)) + 4
                        }}
                    >
                        <TabulatorTable
                            cols={data.columns}
                            data={data.rows.FreeAgentType}
                            dataTreeElementColumn={'Category'}
                            initSort={[{ column: 'SigningDate', dir: 'desc' }]}
                        />
                        <br />
                    </div>
                    <div
                        className='pdfChunk tableChunk'
                        style={{
                            width: (data.columns.reduce((acc, cur) => acc + ((cur.width) > 20 ? (cur.width) : 20), 0)) + 4
                        }}
                    >
                        <TabulatorTable
                            cols={data.columns}
                            data={data.rows.Total}
                            dataTreeElementColumn={'Category'}
                            initSort={[{ column: 'SigningDate', dir: 'desc' }]}
                        />
                        <br />
                    </div>
                </>
            }
            {
                data?.exceptionMsg &&
                <React.Fragment>
                    <p style={{ color: 'rgb(220, 53, 69)', textAlign: 'center' }}>{data.exceptionMsg}</p>
                    <br />
                    <br />
                </React.Fragment>
            }
        </div>
    );
}

export default FreeAgencyReport;


function RecentSigningsForm(props) {
    const [filters, setFilters] = useState([]);

    useEffect(() => {
        if (props.filters == null)
            return;

        let myFilters = [];

        props.filters
            .forEach((column, filterIndex) => {
                let filter = Object.assign(
                    {},
                    createFilter(
                        filterIndex,
                        column,
                        (new Date()).getMonth() >= 10 ? (new Date()).getFullYear() + 1 : (new Date()).getFullYear(),
                    )
                );
                if (filter.formInputs.inputType === "PersonSelect") {
                    filter.clearAfterSelection = true;
                }
                if (filter.statId === 2 && filter.value == null)
                    filter.value = (new Date()).getFullYear() + 1;
                myFilters.push(filter);
            });

        setFilters(myFilters);
    }, [props.filters]);

    function setFilter(filterIndex, type, value) {
        let newFilters = filters.slice();
        let filterToUpdate = newFilters.find(filter => filter.index === filterIndex)
        filterToUpdate[type] = value;
        setFilters(newFilters);
    }

    function submitForm() {
        let formOptions = {
            filters: filters,
        };

        props.submit(formOptions);
    }

    return (
        <React.Fragment>
            <h5>Advanced Filters</h5>
            <div className='advanced-filter'>
                {
                    filters.map(filter =>
                        <StatFilter
                            filter={filter}
                            lastSeason={(new Date()).getMonth() >= 10 ? (new Date()).getFullYear() + 1 : (new Date()).getFullYear()}
                            people={props.allPlayers}
                            setFilter={setFilter}
                        />
                    )
                }
            </div>

            <br />
            <Row className='pdfIgnore'>
                <Button variant='success'
                    onClick={submitForm}
                >
                    Submit
                </Button>
                <div style={{ margin: '0px 0px 0px auto' }}>
                    {props.exporters}
                </div>
            </Row>
        </React.Fragment>
    );
}